import * as React from 'react';
import {
  CircleNotch,
  MagnifyingGlass,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Input } from '@fragments-sdk/ui';
import { useSearch } from '@tanstack/react-router';
import {
  ContentState,
  DashboardLayout,
  WorkspacePage,
  WorkspacePageHeader,
} from '@/components/layout';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  type FindingsGroupBy,
  type FindingsSearch,
  type FindingsSeverity,
  type FindingsSort,
  type FindingsStatusFilter,
  type FindingsView,
} from '../findings-url';
import { FindingDetailPanel } from '../finding-detail-panel';
import {
  FindingsActionContext,
  FindingsRowStaticContext,
  type FindingsActionContextValue,
  type FindingsRowStaticContextValue,
} from '../findings-table';
import type { FindingDoc } from '../findings-types';
import { WaiverDialog } from '../waiver-dialog';
import { FindingsScopeSwitch } from '../findings-navigation';
import { useFindingsScopeNav } from '../use-findings-scope-nav';
import { ScanProgressBanner } from '@/components/scan-progress';
import { AutofixSuccessBanner } from './autofix-success-banner';
import { useManualRescan } from './use-manual-rescan';
import { renderFindingsHeaderActions } from './header-actions';
import { useFindingsUpdateSearch } from '../use-findings-update-search';
import { plural } from '../findings-display-model';
import {
  hasCloudPolicyManagement,
  hasGovernanceAutomation,
  hasFeature,
  type PlanId,
} from '../../../../../convex/billing/plans';
import s from '../findings.module.scss';
import {
  FindingsActiveFilters,
  FindingsFilterPopover,
  WorkbenchSegment,
} from './filter-popover';
import { RepositoryFindingsTable } from './repository-table';
import { useStableCallback } from './use-stable-callback';
import { RepositoryBulkActionBar } from './repository-bulk-action-bar';
import { RepositoryFindingsScoreboard } from './repository-scoreboard';
import { useRepositoryFindingActions } from './use-repository-finding-actions';
import { useRepositoryFindingsQuery } from './use-repository-findings-query';
import { useRepositoryTableModel } from './use-repository-table-model';
import { useRepositoryWorkbenchState } from './use-repository-workbench-state';
import { resolveDefaultFindingsGroupBy } from './repository-view-model';

// ── Filter config ────────────────────────────────────────────────
const DIRECT_AUTOFIX_LIMIT = 5;
const REVIEW_AUTOFIX_LIMIT = 25;
const REVIEW_AUTOFIX_FILE_LIMIT = 10;
const FINDINGS_PAGE_SIZE = 25;

// ── Clipboard helper ─────────────────────────────────────────────

function copyToClipboard(text: string) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

// ── Component ────────────────────────────────────────────────────

export function RepositoryFindingsPage() {
  const search = useSearch({ strict: false }) as FindingsSearch;
  const updateSearch = useFindingsUpdateSearch();
  const { goToBacklog, goToPullRequests } = useFindingsScopeNav();
  const { org, activeRole } = useOrgContext();
  const findingsReadOnly = Boolean(
    (org as { isDemo?: boolean } | undefined)?.isDemo,
  );
  const orgPlan = (org?.plan ?? 'free') as PlanId;
  const canCreateGitHubIssue = hasFeature(orgPlan, 'githubIntegration');
  const canCreateAutofix =
    !findingsReadOnly && hasGovernanceAutomation(orgPlan);
  const canCreateWaiver =
    !findingsReadOnly &&
    hasCloudPolicyManagement(orgPlan) &&
    (activeRole === 'owner' || activeRole === 'admin');
  const {
    selectedProjectId,
    selectedProject,
    isLoading: projectLoading,
  } = useProjectContext();

  const handleManualRescan = useManualRescan({
    orgId: org?._id,
    projectId: selectedProjectId,
    repoFullName: selectedProject?.repoFullName,
  });

  const activeFilter: FindingsStatusFilter = search.status ?? 'open';
  const activeView: FindingsView = search.view ?? 'all';
  const searchQuery = search.q ?? '';
  // Local state for the search box, debounced into the URL. Without
  // this, every keystroke runs the full client-side filter pipeline
  // and triggers a router navigation; with it, both wait until the
  // user stops typing.
  const [searchInput, setSearchInput] = React.useState(searchQuery);
  React.useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  const activeRule = search.rule ?? null;
  const activeCode = search.code ?? null;
  const activeCategory = search.category ?? null;
  const activeSeverity: FindingsSeverity | null = search.severity ?? null;
  const activeTokenFilter = search.token ?? null;
  const activeComponentFilter = search.component ?? null;
  const activeAreaFilter = search.area ?? null;
  const activeDecisionActionability = search.actionability ?? null;
  const activeDecisionPolicyStatus = search.policyStatus ?? null;
  const activeDecisionKind = search.decisionKind ?? null;
  const activeDecisionKey = search.decisionKey ?? null;
  const sortBy: FindingsSort = search.sort ?? 'default';
  const defaultGroupBy: FindingsGroupBy = resolveDefaultFindingsGroupBy({
    activeFilter,
    activeView,
    activeRule,
    activeCode,
    activeCategory,
    activeSeverity,
    activeTokenFilter,
    activeComponentFilter,
    searchQuery,
  });
  const groupBy: FindingsGroupBy = search.groupBy ?? defaultGroupBy;
  const currentPage = Math.max(1, search.page ?? 1);
  const pageIndex = currentPage - 1;

  // Debounce the search box → URL by ~200ms. The trailing edge wins,
  // so consecutive keystrokes coalesce into a single navigation +
  // re-filter pass.
  React.useEffect(() => {
    if (searchInput === searchQuery) return;
    const t = setTimeout(() => {
      updateSearch({ q: searchInput || undefined });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, searchQuery]);

  const {
    findingsPage,
    counts,
    groupAggregate,
    latestRepoScanRun,
    isLoading,
    sortedFindings,
    sourceFilter,
    componentRegistryLoading,
    componentFilterArg,
    actions: { updateStatus, bulkUpdateStatus, createWaiver, bulkLinkIssue },
  } = useRepositoryFindingsQuery({
    selectedProjectId,
    projectLoading,
    activeFilter,
    activeView,
    activeRule,
    activeCode,
    activeCategory,
    activeSeverity,
    activeTokenFilter,
    activeComponentFilter,
    activeAreaFilter,
    activeDecisionActionability,
    activeDecisionPolicyStatus,
    activeDecisionKind,
    activeDecisionKey,
    groupBy,
    pageIndex,
    pageSize: FINDINGS_PAGE_SIZE,
    searchQuery,
    sortBy,
  });

  const [fixPlanOpen, setFixPlanOpen] = React.useState(false);
  const [fixPlanCategory, setFixPlanCategory] = React.useState<string | null>(
    null,
  );
  const contentRef = React.useRef<HTMLDivElement>(null);

  function toggleSort(col: FindingsSort) {
    updateSearch({ sort: sortBy === col ? undefined : col });
  }

  const tableModel = useRepositoryTableModel({
    selectedProjectId,
    componentRegistryLoading,
    groupBy,
    activeFilter,
    activeView,
    activeRule,
    activeCode,
    activeCategory,
    activeSeverity,
    activeTokenFilter,
    activeAreaFilter,
    activeDecisionActionability,
    activeDecisionPolicyStatus,
    activeDecisionKind,
    activeDecisionKey,
    sourceFilter,
    componentFilterArg,
    searchQuery,
    sortedFindings,
    findingsPage,
    groupAggregate,
    pageIndex,
    currentPage,
    pageSize: FINDINGS_PAGE_SIZE,
    directAutofixLimit: DIRECT_AUTOFIX_LIMIT,
    reviewAutofixLimit: REVIEW_AUTOFIX_LIMIT,
    reviewAutofixFileLimit: REVIEW_AUTOFIX_FILE_LIMIT,
    canCreateAutofix,
    fixPlanOpen,
    fixPlanCategory,
    setFixPlanOpen,
    setFixPlanCategory,
    contentRef,
    updateSearch,
  });
  const {
    rowSelection,
    setRowSelection,
    expandedGroups,
    toggleGroupExpanded,
    groupChildrenLoading,
    selectionRefs,
    table,
    selectedIds,
    selectedOpenIds,
    selectedFixableIds,
    selectedResolvedOrIgnoredIds,
    selectedFindings,
    selectedFixPlanMode,
    selectedFixableCategoryBatches,
    selectedCategoryBatch,
    plannedFixIds,
    plannedFixCount,
    plannedFixFileCount,
    canCreatePlannedFix,
    fixPlanVisible,
    selectedSummaryParts,
    totalPaginatedItems,
    totalPages,
    pageStart,
    pageEnd,
    virtualRows,
    paddingTop,
    paddingBottom,
    measureRow,
    safeFixIds,
  } = tableModel;
  const matchedFindingsCount =
    groupBy === 'none'
      ? totalPaginatedItems
      : (groupAggregate?.totalMatching ?? sortedFindings.length);
  const resultSummary =
    groupBy === 'none'
      ? `${plural(totalPaginatedItems, 'finding')} in this view`
      : `${plural(totalPaginatedItems, 'group')} in this view · ${plural(
          matchedFindingsCount,
          'finding',
        )} matched`;
  const {
    fixLoading,
    fixResult,
    issueLoading,
    waiverDraft,
    detailFinding,
    setWaiverDraft,
    setDetailFinding,
    handleCopy,
    handleBulkStatus,
    handleFindingStatus,
    handleAutofix,
    clearFixResult,
    openWaiverDialog,
    submitWaiverDraft,
    handleCreateIssue,
  } = useRepositoryFindingActions({
    selectedProjectId,
    org,
    orgPlan,
    repoFullName: selectedProject?.repoFullName,
    readOnly: findingsReadOnly,
    canCreateAutofix,
    canCreateWaiver,
    canCreateGitHubIssue,
    updateStatus,
    bulkUpdateStatus,
    createWaiver,
    bulkLinkIssue,
    copyToClipboard,
    onIssueCreated: () => setRowSelection({}),
  });

  // Stable handlers for cells consumed via FindingsRowStaticContext.
  // Bodies always see latest closures via useStableCallback.
  const copy = useStableCallback((text: string) => handleCopy(text));
  const fix = useStableCallback((id: string) => {
    void handleAutofix([id]);
  });
  const ignore = useStableCallback((finding: FindingDoc) =>
    openWaiverDialog(finding),
  );
  const reopen = useStableCallback((id: string) => {
    void handleFindingStatus(id, 'open');
  });
  const createIssue = useStableCallback((finding: FindingDoc) => {
    void handleCreateIssue([finding]);
  });
  const inspect = useStableCallback((finding: FindingDoc) => {
    setDetailFinding(finding);
  });
  const toggleSortStable = useStableCallback((col: FindingsSort) =>
    toggleSort(col),
  );

  const rowStaticContextValue = React.useMemo<FindingsRowStaticContextValue>(
    () => ({
      activeView,
      groupBy,
      sortBy,
      readOnly: findingsReadOnly,
      canCreateAutofix,
      canCreateIssue: canCreateGitHubIssue,
      canCreateWaiver,
      repoFullName: selectedProject?.repoFullName,
      repoRef: selectedProject?.defaultBranch,
      toggleSort: toggleSortStable,
      copy,
      fix,
      ignore,
      reopen,
      createIssue,
      inspect,
      selection: selectionRefs,
    }),
    [
      activeView,
      groupBy,
      sortBy,
      findingsReadOnly,
      canCreateAutofix,
      canCreateGitHubIssue,
      canCreateWaiver,
      selectedProject?.repoFullName,
      selectedProject?.defaultBranch,
      toggleSortStable,
      copy,
      fix,
      ignore,
      reopen,
      createIssue,
      inspect,
      selectionRefs,
    ],
  );

  const actionContextValue = React.useMemo<FindingsActionContextValue>(
    () => ({ fixLoading, fixResult, issueLoading }),
    [fixLoading, fixResult, issueLoading],
  );

  const {
    hasContentFilters,
    clearContentFilters,
    noVisibleFindings,
    emptyTitle,
    emptyDescription,
    primaryAction,
    statusOptions,
    groupOptions,
    sortOptions,
    activeFilterChips,
    popoverFilterCount,
  } = useRepositoryWorkbenchState({
    activeFilter,
    activeView,
    activeRule,
    activeCode,
    activeCategory,
    activeSeverity,
    activeTokenFilter,
    activeComponentFilter,
    activeAreaFilter,
    activeDecisionActionability,
    activeDecisionPolicyStatus,
    activeDecisionKind,
    activeDecisionKey,
    groupBy,
    defaultGroupBy,
    sortBy,
    searchQuery,
    safeFixIds,
    sortedFindings,
    counts,
    groupAggregate,
    latestRepoScanRun,
    selectedProjectId,
    fixLoading,
    fixResult,
    canCreateAutofix,
    findingsReadOnly,
    reviewAutofixLimit: REVIEW_AUTOFIX_LIMIT,
    setSearchInput,
    updateSearch,
    actions: { handleAutofix, handleBulkStatus },
  });

  if (projectLoading || (!!selectedProjectId && counts === undefined)) {
    return (
      <ContentState
        className={s.loadingState}
        icon={<CircleNotch size={18} className={s.spinIcon} />}
        title="Loading findings…"
        minHeight={320}
      />
    );
  }

  return (
    <FindingsRowStaticContext.Provider value={rowStaticContextValue}>
      <FindingsActionContext.Provider value={actionContextValue}>
        <DashboardLayout activeNav="Backlog" flush>
          <WorkspacePage className={s.contentPanel} ref={contentRef}>
            <WorkspacePageHeader
              title="Findings"
              description={
                selectedProject?.name
                  ? `Review design-system drift in ${selectedProject.name}, inspect affected source files, and apply token-safe fixes.`
                  : 'Review design-system drift, inspect affected source files, and apply token-safe fixes.'
              }
              actions={renderFindingsHeaderActions({
                primaryAction,
                selectedProjectId,
                repoFullName: selectedProject?.repoFullName,
                readOnly: findingsReadOnly,
                onRescan: handleManualRescan,
              })}
            />

            <ScanProgressBanner
              projectId={selectedProjectId}
              repoFullName={selectedProject?.repoFullName}
            />

            <RepositoryFindingsScoreboard
              open={counts?.open ?? 0}
              resolved={counts?.resolved ?? 0}
              ignored={counts?.ignored ?? 0}
              matched={matchedFindingsCount}
              safeFixes={safeFixIds.length}
            />

            {fixResult && (
              <AutofixSuccessBanner
                result={fixResult}
                onDismiss={clearFixResult}
              />
            )}

            <FindingsScopeSwitch
              activeScope="repository"
              onRepository={goToBacklog}
              onPullRequests={goToPullRequests}
            />

            <div className={s.workbenchToolbar}>
              <WorkbenchSegment
                label="Status"
                value={
                  activeFilter === 'all' ? ('open' as const) : activeFilter
                }
                options={statusOptions}
                onChange={(status) =>
                  updateSearch({
                    status: status === 'open' ? undefined : status,
                  })
                }
              />
              <WorkbenchSegment
                label="Group"
                value={groupBy}
                options={groupOptions}
                onChange={(nextGroupBy) =>
                  updateSearch({
                    groupBy:
                      nextGroupBy === defaultGroupBy ? undefined : nextGroupBy,
                    actionability:
                      nextGroupBy === 'decision'
                        ? (activeDecisionActionability ?? undefined)
                        : undefined,
                    policyStatus:
                      nextGroupBy === 'decision'
                        ? (activeDecisionPolicyStatus ?? undefined)
                        : undefined,
                    decisionKind:
                      nextGroupBy === 'decision'
                        ? (activeDecisionKind ?? undefined)
                        : undefined,
                    decisionKey:
                      nextGroupBy === 'decision'
                        ? (activeDecisionKey ?? undefined)
                        : undefined,
                  })
                }
              />
              <WorkbenchSegment
                label="Sort"
                value={sortBy}
                options={sortOptions}
                onChange={(sort) =>
                  updateSearch({ sort: sort === 'default' ? undefined : sort })
                }
              />
            </div>

            <div className={s.triageBar}>
              <div className={s.triageMain}>
                <Input
                  className={s.triageSearch}
                  type="text"
                  size="sm"
                  placeholder="Search findings..."
                  value={searchInput}
                  onValueChange={setSearchInput}
                  aria-label="Search findings"
                  startAdornment={<MagnifyingGlass size={13} />}
                  withFieldWrapper={false}
                />
                <div className={s.triageFilters}>
                  <FindingsActiveFilters
                    filters={activeFilterChips}
                    onClear={updateSearch}
                  />
                </div>
                <FindingsFilterPopover
                  activeView={activeView}
                  activeSeverity={activeSeverity}
                  activeRule={activeRule}
                  activeCategory={activeCategory}
                  activeArea={activeAreaFilter}
                  activeDecisionActionability={activeDecisionActionability}
                  activeDecisionPolicyStatus={activeDecisionPolicyStatus}
                  activeDecisionKind={activeDecisionKind}
                  groupBy={groupBy}
                  counts={counts}
                  activeCount={popoverFilterCount}
                  onUpdate={updateSearch}
                  onClear={clearContentFilters}
                />
              </div>
            </div>

            {!isLoading && !noVisibleFindings && (
              <div className={s.workbenchResultSummary}>
                <span>{resultSummary}</span>
                <span>
                  {activeFilter === 'open'
                    ? `${plural(
                        safeFixIds.length,
                        'safe fix',
                        'safe fixes',
                      )} available`
                    : `Status: ${activeFilter}`}
                </span>
              </div>
            )}

            {isLoading ? (
              <div className={s.loadingState}>
                <CircleNotch size={18} className={s.spinIcon} />
                Loading findings&hellip;
              </div>
            ) : noVisibleFindings ? (
              <ContentState
                className={s.emptyState}
                icon={<ShieldCheck size={40} />}
                title={emptyTitle}
                description={emptyDescription}
                action={
                  hasContentFilters ? (
                    <button
                      type="button"
                      className={s.detailSecondaryButton}
                      onClick={clearContentFilters}
                    >
                      Clear filters
                    </button>
                  ) : undefined
                }
                minHeight={280}
              />
            ) : (
              <RepositoryFindingsTable
                table={table}
                groupBy={groupBy}
                virtualRows={virtualRows}
                paddingTop={paddingTop}
                paddingBottom={paddingBottom}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                measureRow={measureRow}
                expandedGroups={expandedGroups}
                groupChildrenLoading={groupChildrenLoading}
                repoFullName={selectedProject?.repoFullName}
                repoRef={selectedProject?.defaultBranch}
                readOnly={findingsReadOnly}
                fixLoading={fixLoading}
                fixPrCreated={Boolean(fixResult)}
                canCreateWaiver={canCreateWaiver}
                currentPage={currentPage}
                totalPages={totalPages}
                pageStart={pageStart}
                pageEnd={pageEnd}
                totalItems={totalPaginatedItems}
                onToggleGroup={toggleGroupExpanded}
                onAutofix={handleAutofix}
                onBulkStatus={handleBulkStatus}
                onPageChange={(page) => updateSearch({ page })}
              />
            )}

            {!findingsReadOnly && (
              <RepositoryBulkActionBar
                selectedIds={selectedIds}
                selectedSummaryParts={selectedSummaryParts}
                selectedOpenIds={selectedOpenIds}
                selectedFixableIds={selectedFixableIds}
                selectedResolvedOrIgnoredIds={selectedResolvedOrIgnoredIds}
                selectedFindings={selectedFindings}
                selectedFixPlanMode={selectedFixPlanMode}
                selectedFixableCategoryBatches={selectedFixableCategoryBatches}
                selectedCategoryBatch={selectedCategoryBatch}
                fixPlanVisible={fixPlanVisible}
                fixPlanCategory={fixPlanCategory}
                fixLoading={fixLoading}
                issueLoading={issueLoading}
                fixPrCreated={Boolean(fixResult)}
                canCreateWaiver={canCreateWaiver}
                canCreateAutofix={canCreateAutofix}
                canCreateGitHubIssue={canCreateGitHubIssue}
                repoFullName={selectedProject?.repoFullName}
                reviewAutofixLimit={REVIEW_AUTOFIX_LIMIT}
                reviewAutofixFileLimit={REVIEW_AUTOFIX_FILE_LIMIT}
                plannedFixIds={plannedFixIds}
                plannedFixCount={plannedFixCount}
                plannedFixFileCount={plannedFixFileCount}
                canCreatePlannedFix={canCreatePlannedFix}
                setFixPlanOpen={setFixPlanOpen}
                setFixPlanCategory={setFixPlanCategory}
                setRowSelection={setRowSelection}
                onBulkStatus={handleBulkStatus}
                onAutofix={handleAutofix}
                onCreateIssue={handleCreateIssue}
              />
            )}
          </WorkspacePage>

          {detailFinding && (
            <FindingDetailPanel
              finding={detailFinding}
              projectRepoFullName={selectedProject?.repoFullName}
              onClose={() => setDetailFinding(null)}
              onCopy={handleCopy}
              onFix={(finding) => void handleAutofix([finding._id])}
              onIgnore={openWaiverDialog}
              onReopen={(finding) =>
                void handleFindingStatus(finding._id, 'open')
              }
              canCreateWaiver={canCreateWaiver}
              fixLoading={fixLoading}
            />
          )}

          {waiverDraft && (
            <WaiverDialog
              draft={waiverDraft}
              onChange={setWaiverDraft}
              onCancel={() => setWaiverDraft(null)}
              onSubmit={() => void submitWaiverDraft()}
            />
          )}
        </DashboardLayout>
      </FindingsActionContext.Provider>
    </FindingsRowStaticContext.Provider>
  );
}
